import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  BudgetPlanStyles,
  StatusPill,
  money,
  num,
} from "./BudgetPlanShared";

const emptyLine = () => ({
  id: "",
  chart_of_account_id: "",
  purpose: "",
  amount: "",
});

const EditBudgetPlan = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [list, setList] = useState([emptyLine()]);
  const [chartOfAccount, setChartOfAccount] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // An approved plan is the figure spending is measured against, so the server
  // refuses to change it; the form goes read-only to match.
  const locked = status === "approved";

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const recalculateTotal = (rows) => {
    const total = rows.reduce(
      (sum, item) => sum + (num(item.amount) > 0 ? num(item.amount) : 0),
      0
    );
    setTotalAmount(Number(total.toFixed(2)));
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    const inputInfo = [...list];

    inputInfo[index][name] = value;
    setList(inputInfo);

    if (name === "amount") {
      recalculateTotal(inputInfo);
    }
  };

  const handleAccountChange = (selectedOption, index) => {
    const value = selectedOption ? selectedOption.value : "";
    const alreadyUsed = list.some(
      (item, itemIndex) =>
        itemIndex !== index &&
        String(item.chart_of_account_id) === String(value)
    );

    if (alreadyUsed) {
      Swal.fire(
        "Already budgeted",
        "This chart of account is already on another line of this plan.",
        "warning"
      );
      return;
    }

    const inputInfo = [...list];
    inputInfo[index].chart_of_account_id = value;

    if (!inputInfo[index].purpose && selectedOption) {
      inputInfo[index].purpose = selectedOption.label;
    }

    setList(inputInfo);
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small mg-t-4">{error[fieldName]}</div>;
    }
    return null;
  };

  const addMoreList = () => {
    setList([...list, emptyLine()]);
  };

  const removeList = (index) => {
    const row = list[index];

    // Lines already booked against cannot go; the server rejects it too.
    if (row.consumed > 0) {
      Swal.fire(
        "Already in use",
        "This line has journal vouchers booked against it and cannot be removed.",
        "warning"
      );
      return;
    }

    Swal.fire({
      title: "Remove this line?",
      text: "The budget line will be dropped from this plan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes, remove it",
    }).then((result) => {
      if (result.isConfirmed) {
        const inputInfo = [...list];
        inputInfo.splice(index, 1);
        setList(inputInfo);
        recalculateTotal(inputInfo);
      }
    });
  };

  const formSubmit = () => {
    const missingAccount = list.some((item) => !item.chart_of_account_id);
    if (missingAccount) {
      Swal.fire(
        "Chart of account required",
        "Every budget line needs an account.",
        "warning"
      );
      return;
    }

    setSaving(true);
    setError({});

    Http.put("/budget-plan/" + id, {
      ...inputs,
      total_amount: totalAmount,
      list,
    })
      .then((res) => {
        navigate("/budget-plan", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch(function (e) {
        setSaving(false);
        setError(e.response?.data?.errors || {});
        Swal.fire(
          "Not saved",
          e.response?.data?.message || "Please try again.",
          "warning"
        );
      });
  };

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        const accounts = await Http.get("/chart-of-account-code-2");
        setChartOfAccount(accounts.data);

        const res = await Http.get("/budget-plan/" + id + "/edit");

        setInputs({
          start_date: res.data.start_date,
          end_date: res.data.end_date,
          message: res.data.message,
        });
        setStatus(res.data.status);
        setTotalAmount(res.data.total);

        const listInfo = res.data.items.map((item) => ({
          id: item.id,
          chart_of_account_id: item.chart_of_account_id || "",
          purpose: item.purpose,
          amount: item.amount,
          consumed: item.consumed,
        }));

        setList(listInfo.length ? listInfo : [emptyLine()]);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const selectedAccount = (row) =>
    chartOfAccount.find(
      (option) => String(option.value) === String(row.chart_of_account_id)
    ) || null;

  const spentTotal = useMemo(
    () => list.reduce((sum, item) => sum + num(item.consumed), 0),
    [list]
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <BudgetPlanStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Update Budget Plan{" "}
              {status && <StatusPill status={status} />}
            </h5>
            <p className="text-muted mb-0 tx-12">Plan #{id}</p>
          </div>
          <div className="d-flex gap-2">
            <Link
              to={`/show-budget-plan/${id}`}
              className="btn btn-outline-secondary"
            >
              <i className="fa fa-list mg-r-6"></i>Details
            </Link>
            <Link to="/budget-plan" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          {loading ? (
            <div className="bp-panel">
              <div className="bp-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            <>
              {locked && (
                <div className="bp-notice is-locked">
                  <i className="fa fa-lock"></i>
                  <span>
                    This plan is <strong>approved</strong> and can no longer be
                    edited. Spending is measured against these figures.
                  </span>
                </div>
              )}

              <div className="bp-panel">
                <div className="bp-panel-head">
                  <span>
                    <i className="fa fa-calendar-o me-2"></i>Plan Information
                  </span>
                </div>
                <div className="bp-panel-body">
                  <div className="row">
                    <div className="col-lg-3">
                      <label>
                        Start Date <i className="fa fa-star required"></i>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        name="start_date"
                        value={inputs.start_date || ""}
                        onChange={handleChange}
                        disabled={locked}
                        data-required
                      />
                      {displayError("start_date")}
                    </div>
                    <div className="col-lg-3 mg-t-10 mg-lg-t-0">
                      <label>
                        End Date <i className="fa fa-star required"></i>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        name="end_date"
                        value={inputs.end_date || ""}
                        onChange={handleChange}
                        disabled={locked}
                        data-required
                      />
                      {displayError("end_date")}
                    </div>
                    <div className="col-lg-6 mg-t-10 mg-lg-t-0">
                      <label>Remarks</label>
                      <textarea
                        className="form-control"
                        name="message"
                        rows="1"
                        value={inputs.message || ""}
                        onChange={handleChange}
                        placeholder="What this plan covers"
                        disabled={locked}
                      />
                      {displayError("message")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bp-panel">
                <div className="bp-panel-head">
                  <span>
                    <i className="fa fa-list-ul me-2"></i>Budget Lines
                  </span>
                  <span className="bp-head-note">
                    {list.length} {list.length === 1 ? "line" : "lines"}
                    {spentTotal > 0 && ` · ${money(spentTotal)} already spent`}
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="table bp-line-table">
                    <thead>
                      <tr>
                        <th style={{ width: "44px" }}>#</th>
                        <th style={{ width: "36%" }}>
                          Chart of Account{" "}
                          <i className="fa fa-star required"></i>
                        </th>
                        <th style={{ width: "30%" }}>Purpose</th>
                        <th className="bp-num" style={{ width: "20%" }}>
                          Amount
                        </th>
                        <th style={{ width: "60px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(list).map((x, i) => {
                        const consumed = num(x.consumed);
                        const overspent = consumed > num(x.amount);
                        return (
                          <tr key={x.id || "new-" + i}>
                            <td>
                              <span className="bp-row-index">{i + 1}</span>
                            </td>
                            <td>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                name="chart_of_account_id"
                                options={chartOfAccount}
                                value={selectedAccount(x)}
                                onChange={(selectedOption) =>
                                  handleAccountChange(selectedOption, i)
                                }
                                placeholder="Select Chart of Account"
                                isDisabled={locked}
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                              {displayError(
                                "list." + i + ".chart_of_account_id"
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                name="purpose"
                                value={x.purpose || ""}
                                onChange={(e) => handleInputChange(e, i)}
                                placeholder="Input Purpose"
                                disabled={locked}
                                data-required
                              />
                              {displayError("list." + i + ".purpose")}
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control bp-num"
                                name="amount"
                                value={x.amount || ""}
                                onChange={(e) => handleInputChange(e, i)}
                                placeholder="0.00"
                                disabled={locked}
                                data-required
                              />
                              {displayError("list." + i + ".amount")}
                              {consumed > 0 && (
                                <span
                                  className={`bp-cell-note bp-num${
                                    overspent ? " is-over" : ""
                                  }`}
                                >
                                  Spent so far: {money(consumed)}
                                  {overspent && " — over budget"}
                                </span>
                              )}
                            </td>
                            <td className="text-center">
                              {list.length > 1 && !locked && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => removeList(i)}
                                  title={
                                    consumed > 0
                                      ? "Line has bookings against it"
                                      : "Remove line"
                                  }
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan="3" className="bp-num">
                          Total Budget
                        </th>
                        <th className="bp-num">{money(totalAmount)}</th>
                        <th></th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="bp-panel-body pd-t-14">
                  {displayError("total_amount")}
                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={addMoreList}
                      disabled={locked}
                    >
                      <i className="fa fa-plus mg-r-6"></i>Add Line
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={formSubmit}
                      disabled={locked || saving}
                    >
                      <i className="fa fa-send mg-r-10"></i>
                      {saving ? "Saving..." : "Update Plan"}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditBudgetPlan;
